/**
 * Custom agents — the list, and the agent creator.
 *
 * Route: /prioritization/settings/custom-agents
 *
 * The creator is the template made visible (SPEC-custom-agent-template.md
 * §2 J): an administrator says what the agent should do, or picks a type — one
 * of the built-in behaviors — and names it. "Say what it should do" (registry
 * agent_from_words) drafts the type, the name, the card line, the agent's own
 * words and, for the estimate type, the value it works on; a clamp in the
 * browser keeps the draft to those fields and shows, with its next step,
 * anything no type can do. The words and the reference documents are the same
 * editors the agent's settings page mounts. Every agent gets the same common
 * part (status and model, what it may read, documents, words, review, run,
 * learning); the type adds its own part. Create saves the identity and the
 * settings together, or takes the identity back if the settings fail. Only
 * workspace administrators see the creator; the server checks the same
 * (migration 917).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot, Check, ChevronLeft, ChevronRight, CheckCircle2, CircleSlash, Loader2, Plus, Search, Sparkles } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { Button } from '../../../components/ui/Button';
import { Callout, EmptyState, Field, Select, Switch, TextArea, TextInput } from '../../../components/ds';
import { AgentWordsEditor } from '../../../components/agents/template/AgentWordsEditor';
import { ReferenceDocsEditor } from '../../../components/agents/template/ReferenceDocsEditor';
import { BEHAVIOR_SETTINGS } from '../../../components/agents/behaviors';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useCustomAgentStore } from '../../../stores/prioritization/customAgentStore';
import { useAgentSettingsStore } from '../../../stores/prioritization/agentSettingsStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useValueAiSettingsStore } from '../../../stores/prioritization/valueAiSettingsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getItemCountState } from '../../../components/collection/types';
import type { CustomAgent } from '../../../lib/customAgentService';
import { nonEmptyReferenceDocs, type AgentReferenceDoc, type AgentSettings } from '../../../lib/agentSettingsService';
import { setValueAiSetting } from '../../../lib/valueAiService';
import { isWorkspaceEntitledToAi } from '../../../lib/ai/aiAssistSettings';
import { useAiCapability } from '../../../lib/ai/useAiCapability';
import { useAiAssistSettingsStore } from '../../../stores/prioritization/aiAssistSettingsStore';
import { useModuleGate } from '../../../lib/moduleGate';
import { useAiControl } from '../../../lib/ai/aiAvailability';
import { AskAiNeedsKey } from '../../../components/shared/AskAiNeedsKey';
import { AGENT_FROM_WORDS_CAPABILITY_ID, agentFromWords, type AgentDraftValue, type CouldNotSet } from '../../../lib/ai/agentFromWords';
import { describeAgent } from '../../../lib/ai/describeAgent';
import {
  AGENT_BEHAVIORS,
  CUSTOMIZABLE_BEHAVIORS,
  customAgentType,
  definitionForCustomAgent,
  templateSettingsDefaults,
  type AgentBehaviorKind,
} from '../../../lib/ai/agentDefinitions';

/** The part every agent has, whatever its type — the template's common switches. */
const COMMON_SETTINGS = [
  'On or off, and which model it uses',
  'What it may read, and what leaves the workspace when it does',
  'Reference documents it reads before it works',
  'What it should know, in your own words',
  'Who reviews what it produces',
  'When it runs: from an item, on a batch, or on new items',
  'Rules it learns from corrections, which a person approves',
  'Its track record, and every decision it made',
];

function AgentRow({ agent }: { agent: CustomAgent }) {
  const agentType = customAgentType(agent.id);
  const enabled = useAgentSettingsStore((s) => s.byAgent[agentType]?.config?.enabled ?? false);
  const fetchConfig = useAgentSettingsStore((s) => s.fetchConfig);
  useEffect(() => {
    void fetchConfig(agent.workspaceId, agentType);
  }, [agent.workspaceId, agentType, fetchConfig]);

  return (
    <Link
      to={`/prioritization/settings/custom-agents/${agent.id}`}
      className="card flex items-start gap-4 no-underline transition-all hover:border-[var(--slate)] group"
    >
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center bg-[var(--slate-dim)] text-[var(--text-sec)]">
        <Bot className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-[var(--text-pri)] group-hover:text-[var(--text-ter)] transition-colors">
            {agent.name}
          </span>
          <span
            className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium inline-flex items-center gap-1 ${
              enabled ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--surface)] text-[var(--text-ter)]'
            }`}
          >
            {enabled ? <CheckCircle2 className="h-3 w-3" /> : <CircleSlash className="h-3 w-3" />}
            {enabled ? 'On' : 'Off'}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-[var(--text-ter)]">{AGENT_BEHAVIORS[agent.behaviorKind].label}</p>
        {agent.description && (
          <p className="mt-1 text-xs leading-relaxed text-[var(--text-ter)] line-clamp-2">{agent.description}</p>
        )}
      </div>
      <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
    </Link>
  );
}

/** The four starter sentences: the first example of each type the creator offers. */
const STARTERS: { kind: AgentBehaviorKind; sentence: string }[] = CUSTOMIZABLE_BEHAVIORS
  .filter((b) => b.examples.length)
  .map((b) => ({ kind: b.kind, sentence: b.examples[0] }));

/**
 * "Say what it should do": drawn only when a model is known to answer — the
 * person's own key in this browser, or the deployment saying it can call one
 * (useAiControl, the one draw rule for every AI control). Otherwise (no model,
 * or the check failed or could not be read) the box is not drawn and the one
 * no-model line (AskAiNeedsKey) stands in its place (build contract §2.8:
 * never fail late). Nothing is drawn while the check runs or the workspace's
 * assist settings load, so no line flashes first; and the line names the right
 * fix — the plan, when the plan has no AI, else the AI Assists switch.
 */
function SayAnAgent({ workspaceId, entitled, values, drafting, onDraft }: {
  workspaceId: string;
  entitled: boolean;
  values: AgentDraftValue[];
  drafting: boolean;
  onDraft: (said: string, values: AgentDraftValue[]) => Promise<{ reason?: string; couldNotSet: CouldNotSet[] }>;
}) {
  // The one reactive check for an assist (it loads the workspace's settings and re-renders when they change).
  const sayingOn = useAiCapability(AGENT_FROM_WORDS_CAPABILITY_ID);
  // The one draw rule for every AI control: drawn only when a model is known to answer.
  const { drawable, needsKey } = useAiControl(sayingOn);
  const assistsLoaded = useAiAssistSettingsStore((s) => s.byWorkspace[workspaceId]?.loadedWorkspaceId === workspaceId);
  const [said, setSaid] = useState('');
  const [reason, setReason] = useState<string | null>(null);
  const [couldNotSet, setCouldNotSet] = useState<CouldNotSet[]>([]);

  if (!entitled) {
    return (
      <p className="text-sm text-[var(--text-ter)]" data-testid="say-an-agent-no-plan">
        This workspace's plan does not include AI features, so saying what an agent should do is not available. Pick a type below.
      </p>
    );
  }
  if (!assistsLoaded) return null;
  if (!sayingOn) {
    return (
      <p className="text-sm text-[var(--text-ter)]" data-testid="say-an-agent-off">
        Saying what an agent should do is off for this workspace. Turn it on under{' '}
        <Link to="/prioritization/settings/ai-assists" className="underline">Settings → AI Assists</Link>, or pick a type below.
      </p>
    );
  }
  if (needsKey) return <AskAiNeedsKey />;
  if (!drawable) return null;

  const draft = async () => {
    setReason(null);
    setCouldNotSet([]);
    const out = await onDraft(said, values);
    setReason(out.reason ?? null);
    setCouldNotSet(out.couldNotSet);
  };

  return (
    <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4 space-y-3" data-testid="say-an-agent">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[var(--text-sec)]" />
        <p className="text-sm font-semibold text-[var(--text-pri)]">Say what it should do</p>
      </div>
      <TextArea
        id="say-an-agent"
        rows={2}
        value={said}
        onChange={(e) => setSaid(e.target.value)}
        placeholder="e.g. An agent that sizes work the way our platform team does: two engineers a sprint, anything touching billing at least Medium."
        aria-label="Say what it should do"
      />
      <div className="flex flex-wrap gap-2">
        {STARTERS.map((st) => (
          <button
            key={st.kind}
            type="button"
            onClick={() => setSaid(st.sentence)}
            className="border border-[var(--rule)] bg-[var(--bg-raised)] px-2.5 py-1 text-xs text-[var(--text-sec)] hover:border-[var(--slate)] hover:text-[var(--text-pri)]"
            data-testid="say-an-agent-starter"
          >
            {st.sentence}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-[var(--text-ter)]">What goes to the model: your sentence, the four type names with their summaries and examples, and the names of your values. No item content. The draft fills the form below, where you change anything before Create.</p>
        <Button variant="secondary" size="sm" disabled={drafting || !said.trim()} onClick={() => void draft()}>
          {drafting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} {drafting ? 'Drafting…' : 'Draft it'}
        </Button>
      </div>
      {reason && <Callout tone="coral">{reason}</Callout>}
      {couldNotSet.length > 0 && (
        <Callout tone="neutral" title="Not set — the next step for each">
          <ul className="space-y-1" data-testid="say-an-agent-could-not-set">
            {couldNotSet.map((c, i) => (
              <li key={i} className="text-sm text-[var(--text-sec)]">
                “{c.asked}” — {c.href ? <Link to={c.href} className="underline">{c.nextStep}</Link> : c.nextStep}
              </li>
            ))}
          </ul>
        </Callout>
      )}
    </div>
  );
}

function AgentCreator({ workspaceId, onCancel }: { workspaceId: string; onCancel: () => void }) {
  const saveAgent = useCustomAgentStore((s) => s.saveAgent);
  const deleteAgent = useCustomAgentStore((s) => s.deleteAgent);
  const saveConfig = useAgentSettingsStore((s) => s.saveConfig);
  const userId = useUserStore((s) => s.userId);
  const navigate = useNavigate();

  const attributes = useAttributesStore((s) => s.attributes);
  const loadValueAi = useValueAiSettingsStore((s) => s.load);
  const valueAi = useValueAiSettingsStore((s) => s.settings);
  const agentName = useValueAiSettingsStore((s) => s.agentName);
  const putValueAi = useValueAiSettingsStore((s) => s.put);
  useEffect(() => { void loadValueAi(workspaceId); }, [workspaceId, loadValueAi]);
  // The same plan check the agent's settings page makes before it lets anyone turn an agent on.
  const entitled = isWorkspaceEntitledToAi(workspaceId);
  const reportsModuleOn = useModuleGate('reports');

  const [kind, setKind] = useState<AgentBehaviorKind>(CUSTOMIZABLE_BEHAVIORS[0].kind);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [words, setWords] = useState('');
  const [docs, setDocs] = useState<AgentReferenceDoc[]>([]);
  const [valueKey, setValueKey] = useState('');
  const [useForValue, setUseForValue] = useState(false);
  const [turnOn, setTurnOn] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; agentHref?: string } | null>(null);
  const behavior = AGENT_BEHAVIORS[kind];
  const copy = BEHAVIOR_SETTINGS[kind];

  // The values an estimate agent may work on: the same list its settings page offers.
  const values: AgentDraftValue[] = useMemo(
    () => attributes.filter((a) => !a.isCustomField && (a.scales?.length || a.options?.length)).map((a) => ({ key: a.key, label: a.label })),
    [attributes],
  );
  const value = values.find((v) => v.key === valueKey) ?? null;
  const valueSetting = value ? valueAi.find((v) => v.attributeKey === value.key) ?? null : null;
  const replaces = valueSetting && valueSetting.mode !== 'off' && valueSetting.agentType ? agentName(valueSetting.agentType) : null;

  const handleDraft = async (said: string, vals: AgentDraftValue[]) => {
    setDrafting(true);
    try {
      const out = await agentFromWords(said, { values: vals, reportsModuleOn });
      if (!out.ok) return { reason: out.reason, couldNotSet: [] };
      if (out.draft) {
        setKind(out.draft.kind);
        setName(out.draft.name);
        setDescription(out.draft.description);
        setWords(out.draft.words);
        if (out.draft.kind === 'estimate_value' && out.draft.valueKey) {
          setValueKey(out.draft.valueKey);
          setUseForValue(true);
        }
      }
      return { couldNotSet: out.couldNotSet };
    } finally {
      setDrafting(false);
    }
  };

  // The sentence that reads the agent back as it will be made.
  const preview = useMemo(() => {
    const def = definitionForCustomAgent({ id: 'new', name: name.trim() || behavior.label, description: description.trim() || null, behaviorKind: kind });
    const settings: AgentSettings = {
      workspaceId,
      agentType: def.agentType,
      enabled: turnOn,
      model: behavior.base.defaultModel,
      systemPrompt: words.trim() || null,
      autoRunOnCreate: false,
      updatedAt: '',
      ...templateSettingsDefaults(def),
      referenceDocs: nonEmptyReferenceDocs(docs),
    };
    return describeAgent(def, { settings, isNew: true, valueLabels: kind === 'estimate_value' && useForValue && value ? [value.label] : [] });
  }, [kind, name, description, words, docs, turnOn, useForValue, value, behavior, workspaceId]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setError(null);
    setBusy(true);
    try {
      const result = await saveAgent({
        workspaceId,
        name: name.trim(),
        description: description.trim() || null,
        behaviorKind: kind,
        model: behavior.base.defaultModel,
      });
      if (!result.success || !result.agent) {
        setError({ text: result.error ?? 'The agent could not be made.' });
        return;
      }
      const agent = result.agent;
      const agentType = customAgentType(agent.id);
      // Identity and settings together: every switch group's defaults, the words and the documents,
      // so the database enforces its reviewers and limits from the start. If this fails, the identity goes back.
      const def = definitionForCustomAgent(agent);
      const saved = await saveConfig(workspaceId, agentType, {
        enabled: turnOn,
        model: behavior.base.defaultModel,
        systemPrompt: words.trim() || null,
        referenceDocs: nonEmptyReferenceDocs(docs),
        ...(turnOn && userId ? { sponsorUserId: userId } : {}),
        ...templateSettingsDefaults(def),
      }, userId ?? undefined);
      if (!saved.success) {
        const why = saved.error ?? 'unknown error';
        const undone = await deleteAgent(agent.id, workspaceId);
        setError(
          undone.success
            ? { text: `The agent was not made: its settings could not be saved (${why}).` }
            : { text: `${agent.name} was made without its settings (${why}), and it could not be taken back (${undone.error ?? 'unknown error'}). It is in the list: open it to finish its settings, or remove it.` },
        );
        return;
      }
      if (kind === 'estimate_value' && useForValue && value) {
        const { setting, error: valueError } = await setValueAiSetting({
          workspaceId,
          attributeKey: value.key,
          mode: 'recommend',
          agentType,
          acceptFloor: valueSetting?.acceptFloor ?? 0.8,
          reason: `Set when ${agent.name} was made`,
        });
        if (setting) putValueAi(setting);
        if (valueError) {
          setError({ text: `${agent.name} was made, but ${value.label} could not be set to use it: ${valueError}.`, agentHref: def.settingsHref });
          return;
        }
      }
      navigate(def.settingsHref);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card space-y-6" data-testid="agent-creator">
      <div>
        <h3 className="font-heading text-lg font-semibold text-[var(--text-pri)]">Create an agent</h3>
        <p className="text-sm text-[var(--text-ter)] mt-0.5">
          Say what it should do, or pick what it does and name it. You change anything before Create; the rest of its switches are on its page.
        </p>
      </div>

      {error && (
        <Callout tone="coral">
          {error.text}{error.agentHref && <> Set it on <Link to={error.agentHref} className="underline">the agent's page</Link>.</>}
        </Callout>
      )}

      <SayAnAgent workspaceId={workspaceId} entitled={entitled} values={values} drafting={drafting} onDraft={handleDraft} />

      <div className="space-y-2">
        <p className="text-sm font-semibold text-[var(--text-pri)]">1. What kind of agent</p>
        <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Agent type">
          {CUSTOMIZABLE_BEHAVIORS.map((b) => {
            const on = b.kind === kind;
            return (
              <button
                key={b.kind}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setKind(b.kind)}
                className={`text-left border p-4 transition-colors ${on ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--slate)]'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-semibold text-[var(--text-pri)]">{b.label}</span>
                  {on && <Check className="h-4 w-4 text-[var(--text-sec)] flex-shrink-0" />}
                </div>
                <p className="mt-1 text-xs leading-relaxed text-[var(--text-sec)]">{b.summary}</p>
                <ul className="mt-2 space-y-0.5">
                  {b.examples.map((ex) => (
                    <li key={ex} className="text-[11px] text-[var(--text-ter)]">e.g. {ex}</li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="border border-[var(--rule)] p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">Every agent has</p>
          <ul className="mt-2 space-y-1">
            {COMMON_SETTINGS.map((line) => (
              <li key={line} className="text-xs text-[var(--text-sec)]">• {line}</li>
            ))}
          </ul>
        </div>
        <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">“{behavior.label}” adds</p>
          <ul className="mt-2 space-y-1">
            {behavior.ownSettings.map((line) => (
              <li key={line} className="text-xs text-[var(--text-sec)]">• {line}</li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-[var(--text-ter)]">
            It proposes; a person decides. It can only produce what this type produces, and it reads only what this type may read.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-[var(--text-pri)]">2. Name it</p>
        <Field label="Name" required htmlFor="agent-name">
          <TextInput id="agent-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sizing guide" />
        </Field>
        <Field label="What it is for" hint="Shown on its card. The agent itself never reads this." htmlFor="agent-description">
          <TextArea id="agent-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Estimates effort the way the platform team sizes work" />
        </Field>
        {kind === 'estimate_value' && (
          <Field label="Which value it works on" hint={values.length ? 'Each value has its own AI setting; this sets it to recommend when you create the agent.' : 'No values with a scale yet. Add one under Settings → Attributes, or set it later on the agent\'s page.'} htmlFor="agent-value">
            <Select id="agent-value" value={valueKey} onChange={(e) => { setValueKey(e.target.value); setUseForValue(!!e.target.value); }} disabled={!values.length}>
              <option value="">Decide later, on its page</option>
              {values.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </Select>
          </Field>
        )}
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-[var(--text-pri)]">3. What it should know</p>
        <AgentWordsEditor id="agent-words" value={words} onChange={setWords} contract={behavior.base.defaultSystemPrompt} />
        <div>
          <p className="text-xs font-medium text-[var(--text-sec)]">Reference documents</p>
          <p className="text-[11px] text-[var(--text-ter)] mb-2">{copy.docsDescription}</p>
          <ReferenceDocsEditor docs={docs} onChange={setDocs} placeholder={copy.docsPlaceholder} />
        </div>
      </div>

      <div className="space-y-3 border-t border-[var(--rule)] pt-4">
        {kind === 'estimate_value' && value && (
          <Switch
            checked={useForValue}
            onChange={setUseForValue}
            label={`Use it for ${value.label}${replaces ? ` (replaces ${replaces} there)` : ''}`}
            description={`${value.label}'s AI setting becomes "AI recommends" with this agent. A person still accepts each value; "AI provides" is set on the value's own page.`}
          />
        )}
        {entitled ? (
          <Switch
            checked={turnOn}
            onChange={setTurnOn}
            label="Turn on"
            description="It runs when asked, and on the triggers set on its page, in your name as its sponsor. Off: set it up first, then turn it on there."
          />
        ) : (
          <p className="text-sm text-[var(--text-ter)]" data-testid="agent-turn-on-no-plan">
            It is made off. This workspace's plan does not include AI features, so it cannot be turned on until the plan does.
          </p>
        )}
        <p className="text-sm text-[var(--text-sec)] border-l-2 border-[var(--slate)] pl-3" data-testid="agent-read-back">{preview}</p>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void handleCreate()}>
          {busy ? 'Creating…' : 'Create agent'}
        </Button>
      </div>
    </div>
  );
}

export default function CustomAgentsPage() {
  const workspaceId = getCurrentWorkspaceId();
  const can = useUserStore((s) => s.can);
  const canCreate = can('admin.settings');
  const agents = useCustomAgentStore((s) => s.agents);
  const isLoading = useCustomAgentStore((s) => s.isLoading);
  const storeError = useCustomAgentStore((s) => s.error);
  const fetchAgents = useCustomAgentStore((s) => s.fetchAgents);

  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (workspaceId) void fetchAgents(workspaceId);
  }, [workspaceId, fetchAgents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(
      (a) => a.name.toLowerCase().includes(q) || (a.description ?? '').toLowerCase().includes(q),
    );
  }, [agents, search]);

  const countState = getItemCountState(filtered.length);

  return (
    <div className="space-y-6">
      <Link
        to="/prioritization/settings/agents"
        className="inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] no-underline"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> All agents
      </Link>

      <PageHeader
        icon={<Bot className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Custom agents"
        subtitle="Agents your workspace made from a built-in type, with your own name, documents and words."
        actions={
          canCreate && !showCreate && workspaceId ? (
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" /> Create an agent
            </Button>
          ) : undefined
        }
      />

      {storeError && !showCreate && <Callout tone="coral">{storeError}</Callout>}

      {showCreate && workspaceId && <AgentCreator workspaceId={workspaceId} onCancel={() => setShowCreate(false)} />}

      {countState === 'many' && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-ter)]" />
          <TextInput type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search custom agents…" className="pl-9" />
        </div>
      )}

      {isLoading && <p className="text-sm text-[var(--text-ter)]">Loading agents…</p>}

      {!isLoading && countState === 'zero' && !showCreate && (
        <EmptyState
          icon={Bot}
          title="No custom agents yet"
          description={canCreate
            ? 'Make one from a built-in type — for example an estimator that sizes work the way your team does.'
            : 'A workspace administrator can make one from a built-in type.'}
          action={canCreate && workspaceId ? (
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" /> Create an agent
            </Button>
          ) : undefined}
        />
      )}

      {!isLoading && countState !== 'zero' && (
        <div className={countState === 'one' ? 'max-w-xl space-y-3' : 'space-y-3'}>
          {filtered.map((agent) => (
            <AgentRow key={agent.id} agent={agent} />
          ))}
        </div>
      )}
    </div>
  );
}
